import './style.scss';

import { useEffect } from 'react';
import { useLocation } from 'react-router';

import { TypeButton } from '../../components/reusable/button';
import { HvcLoad } from '../../components/reusable/components';
import FormBuilder from '../../components/reusable/form-builder';
import type { IFormComponent } from '../../components/reusable/form-builder/utils';
import { useFormHook } from '../../components/reusable/hooks';
import type { IInputSearchResult } from '../../components/reusable/input-search/utils';
import AppModal from '../../components/reusable/modal';
import { ElectionSVG, PoliticalPartySVG } from '../../components/reusable/svgs';
import { DEFAULT_ORGANIZATION, getUserData, ISADMINLOGGED, ISSUPERADMIN } from '../../constants/global';
import { pageurl } from '../../constants/pageurl';
import { useOrganizationContext } from '../../context/organization';
import { useReduxContext } from '../../context/redux';
import type { IActiveElectionData } from '../../interface/state/ICandidate';
import type { IPartyState } from '../../interface/state/IPartyState';

interface IElectionSelectionFormHook {
	electionId: IInputSearchResult[];
	partyId: IInputSearchResult[];
	observerId: IInputSearchResult[];
}

const ElectionSelectionModal = () => {
	const organization = useOrganizationContext();

	const { states, actions } = useReduxContext();

	const [hookForm] = useFormHook<IElectionSelectionFormHook>({});

	const electionId = hookForm.watch('electionId')?.[0]?.value;
	const party = hookForm.watch('partyId')?.[0];

	const partyId = String(party?.value ?? '') || String(!ISSUPERADMIN ? getUserData().user?.PartyId : DEFAULT_ORGANIZATION.id);
	const partySelected = Boolean(partyId);
	const electionSelected = Boolean(electionId);

	const getCandidateActiveElections = (_searchValue: string | number) => {
		actions?.candidate_getActiveElections({ id: partyId });
	};

	const getPoliticalParty = (searchValue: string | number) => {
		actions?.get_Party({
			query: [
				{
					key: 'Name',
					value: searchValue,
				},
			],
		});
	};

	function formatPartyName(longName: string, shortName: string) {
		return `${longName} (${shortName})`;
	}

	useEffect(() => {
		if (ISADMINLOGGED()) getCandidateActiveElections('');

		// The party field is type-filter-only (searchStopTypeSearch) — without this
		// initial load its options are empty forever and every search shows 0 results.
		if (ISSUPERADMIN) getPoliticalParty('');

		if (DEFAULT_ORGANIZATION.shortName !== 'I-ELECT')
			hookForm.setValue('partyId', [
				{
					label: formatPartyName(DEFAULT_ORGANIZATION.longName, DEFAULT_ORGANIZATION.shortName),
					value: DEFAULT_ORGANIZATION.id,
				},
			]);

		if (!ISSUPERADMIN) {
			hookForm.setValue('partyId', [
				{
					label: formatPartyName(organization.longName, organization.shortName),
					value: getUserData().user?.PartyId ?? '',
				},
			]);
		}
	}, []);

	useEffect(() => {
		if (organization.selectedElection)
			hookForm.setValue('electionId', [
				{
					label: organization.selectedElection.electionName,
					value: organization.selectedElection.electionId,
				},
			]);
	}, [organization.selectedElection]);

	const mapElectionOptions = (i: IActiveElectionData) => ({
		label: i.election.name,
		value: i.election.id,
	});

	const mapPartyOptions = (i: IPartyState) => ({
		label: i.longName + ' (' + i.shortName + ')',
		value: i.id,
	});

	const fc: IFormComponent[] = [
		{
			label: 'Select Political Party',
			component: 'input-search-new',
			id: 'partyId',
			placeHolder: 'Click to Select Party',
			className: 'election-select__field',
			searchFetchResults: getPoliticalParty,
			searchOptions: states?._party?.get_Party?.data?.map(mapPartyOptions),
			searchLoad: states?._party?.get_PartyLoading,
			hide: !ISSUPERADMIN,
			searchStopTypeSearch: true,
			searchOnSelected: (selected) => {
				const id = String(selected?.[0]?.value);
				actions.candidate_getActiveElections({ id });
			},
		},
		{
			label: 'Select Election',
			component: 'input-search-new',
			id: 'electionId',
			placeHolder: 'Click to Select Election',
			className: 'election-select__field',
			searchFetchResults: getCandidateActiveElections,
			searchOptions: states?.candidate_?.candidate_getActiveElections?.data?.map(mapElectionOptions),
			searchLoad: states?.candidate_?.candidate_getActiveElectionsLoading,
			hide: ISSUPERADMIN ? !Number(partyId) : false,
			searchOnSelected: (selected) => {
				const electionId = String(selected?.[0]?.value);
				const constituency = states?.candidate_?.candidate_getActiveElections?.data?.find(
					(i) => String(i.election.id) === electionId
				);
				if (constituency?.election?.constituencyId) {
					actions.constituency_getByID({ id: String(constituency.election.constituencyId) });
				}
			},
		},
	];

	const storeElection = (data: IElectionSelectionFormHook) => {
		const election = data.electionId[0];

		const party = data.partyId[0];

		const stateId = states?.constituency_.constituency_getByID.data?.state.id;

		organization.storeElectionSelection(election, stateId);

		organization.storePartySelection(party);

		organization.modalProps.handleCloseModal(() => {
			window.open(pageurl.MYELECTION, '_self');
		});
	};

	// The Help Centre is where a new Party Admin learns how to choose an election, so the picker must not
	// trap them there. Everywhere else it stays mandatory until an election is chosen.
	const { pathname } = useLocation();
	const onHelpCentre = pathname === pageurl.HELP || pathname.startsWith(`${pageurl.HELP}/`);
	const disableClose = !ISSUPERADMIN && !organization.selectedElection.electionId && !onHelpCentre;

	return (
		<AppModal
			modalProps={organization.modalProps}
			size='medium'
			disableClose={disableClose}
			dialogClassName='election-select-modal__dialog'
			bodyClassName='election-select-modal__body'
		>
			{/* Fragment: AppModal injects `modalProps` into a component child, and HvcLoad spreads it onto its div. */}
			<>
				<HvcLoad
					view
					className='election-select'
				>
					<div className='election-select__hero'>
						<div className='election-select__icon'>
							<ElectionSVG color='#0F1A2E' />
						</div>
						<div className='election-select__headline'>
							<span className='election-select__eyebrow'>Required Setup</span>
							<h3 className='election-select__title'>Election Selection Required</h3>
							<p className='election-select__subtitle'>
								Select your political party and election to unlock system access and analytics.
							</p>
							<div className='election-select__progress'>
								<span className={`election-select__dot ${partySelected ? 'is-complete' : ''}`} />
								<span className={`election-select__dot ${electionSelected ? 'is-complete' : ''}`} />
								<span className='election-select__progress-text'>
									Step {partySelected ? (electionSelected ? '2' : '1') : '1'} of 2
								</span>
							</div>
						</div>
					</div>

					<div className='election-select__form'>
						<FormBuilder
							formComponent={fc}
							hookForm={hookForm}
							min
						/>
					</div>

					<div className='election-select__actions'>
						<div className='election-select__hint'>
							<PoliticalPartySVG />
							<span>Access remains locked until you select an election.</span>
						</div>
						<TypeButton
							title='Continue'
							onClick={hookForm.handleSubmit(storeElection)}
							buttonSize='medium'
							className='election-select__cta'
							disabled={!electionId}
						/>
					</div>
				</HvcLoad>
			</>
		</AppModal>
	);
};

// Not mounted at all for an observer: no modal to open, and no active-elections fetch on load.
const ElectionSelection = () => {
	const organization = useOrganizationContext();

	if (organization.isObserver) return null;

	return <ElectionSelectionModal />;
};

export default ElectionSelection;
